import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  TextField,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  CheckCircle,
  Error,
  Delete,
  VideoFile,
  Image,
  AudioFile,
  InsertDriveFile,
} from '@mui/icons-material';

import apiClient from '../../services/api';
import { MediaType } from '../../types';
import { invalidateMediaData } from '../../utils/cacheInvalidation';

interface FileUpload {
  id: string;
  file: File;
  title: string;
  description: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  result?: any;
}

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
}

const UploadDialog: React.FC<UploadDialogProps> = ({
  open,
  onClose,
  onUploadComplete,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [files, setFiles] = useState<FileUpload[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Поддерживаемые типы файлов
  const acceptedTypes = [
    'video/mp4', 'video/avi', 'video/mov', 'video/mkv', 'video/webm',
    'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
    'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/mp4', 'audio/aac',
    'application/pdf', 'text/html'
  ];

  const maxFileSize = 500 * 1024 * 1024; // 500MB

  // Мутация для загрузки файла
  const uploadMutation = useMutation({
    mutationFn: async ({ file, title, description }: { file: File; title: string; description: string }) => {
      return apiClient.uploadMedia(file, title, description);
    },
    onSuccess: () => {
      invalidateMediaData(queryClient);
    },
  });

  const getFileIcon = (type: string) => {
    if (type.startsWith('video/')) return VideoFile;
    if (type.startsWith('image/')) return Image;
    if (type.startsWith('audio/')) return AudioFile;
    return InsertDriveFile;
  };

  const getFileTypeLabel = (type: string): MediaType => {
    if (type.startsWith('video/')) return MediaType.VIDEO;
    if (type.startsWith('image/')) return MediaType.IMAGE;
    if (type.startsWith('audio/')) return MediaType.AUDIO;
    if (type === 'application/pdf') return MediaType.PDF;
    if (type === 'text/html') return MediaType.HTML;
    return MediaType.IMAGE; // default
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return t('media.upload.invalidType');
    }
    if (file.size > maxFileSize) {
      return t('media.upload.fileTooLarge', { max: formatFileSize(maxFileSize) });
    }
    return null;
  };

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const validFiles: FileUpload[] = [];

    Array.from(newFiles).forEach((file) => {
      const error = validateFile(file);
      if (!error) {
        validFiles.push({
          id: `${Date.now()}-${Math.random()}`,
          file,
          title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
          description: '',
          progress: 0,
          status: 'pending',
        });
      }
    });

    setFiles(prev => [...prev, ...validFiles]);
  }, []);

  // Drag & Drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  }, [addFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles) {
      addFiles(selectedFiles);
    }
    // Reset input
    e.target.value = '';
  }, [addFiles]);

  const handleRemoveFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUpdateFile = (fileId: string, updates: Partial<FileUpload>) => {
    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, ...updates } : f));
  };

  const uploadFile = async (fileUpload: FileUpload): Promise<boolean> => {
    let progressInterval: ReturnType<typeof setInterval> | undefined;

    try {
      handleUpdateFile(fileUpload.id, { status: 'uploading', progress: 0 });

      // Симуляция прогресса (в реальном API нужно использовать прогресс загрузки)
      progressInterval = setInterval(() => {
        handleUpdateFile(fileUpload.id, {
          progress: Math.min(fileUpload.progress + Math.random() * 30, 90)
        });
      }, 500);

      const result = await uploadMutation.mutateAsync({
        file: fileUpload.file,
        title: fileUpload.title,
        description: fileUpload.description,
      });

      handleUpdateFile(fileUpload.id, {
        status: 'completed',
        progress: 100,
        result
      });
      return true;

    } catch (error: any) {
      handleUpdateFile(fileUpload.id, {
        status: 'error',
        progress: 0,
        error: error.message || t('media.upload.failed')
      });
      return false;
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    }
  };

  const handleUploadAll = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    let successCount = 0;

    // Загружаем файлы последовательно (можно сделать параллельно если нужно)
    for (const file of pendingFiles) {
      const uploaded = await uploadFile(file);
      if (uploaded) {
        successCount += 1;
      }
    }

    if (successCount > 0) {
      await invalidateMediaData(queryClient);
    }

    if (successCount === pendingFiles.length && pendingFiles.length > 0 && onUploadComplete) {
      onUploadComplete();
    }
  };

  const handleClose = () => {
    // Проверяем, есть ли загружающиеся файлы
    const hasUploading = files.some(f => f.status === 'uploading');
    if (hasUploading) {
      if (confirm(t('media.upload.confirmClose'))) {
        setFiles([]);
        onClose();
      }
    } else {
      setFiles([]);
      onClose();
    }
  };

  const completedCount = files.filter(f => f.status === 'completed').length;
  const errorCount = files.filter(f => f.status === 'error').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const pendingCount = files.filter(f => f.status === 'pending').length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {t('media.upload.title')}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Drop Zone */}
        <Card
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'grey.300',
            backgroundColor: dragOver ? 'action.hover' : 'background.paper',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            mb: 3,
            p: 3,
            textAlign: 'center',
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <CardContent>
            <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('media.upload.dropZone')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('media.upload.dropZoneDescription')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('media.upload.supportedFormats')}
            </Typography>
          </CardContent>
        </Card>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {/* Статистика */}
        {files.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('media.upload.statistics')}:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {pendingCount > 0 && (
                <Chip label={t('media.upload.pending', { count: pendingCount })} color="default" size="small" />
              )}
              {uploadingCount > 0 && (
                <Chip label={t('media.upload.uploading', { count: uploadingCount })} color="primary" size="small" />
              )}
              {completedCount > 0 && (
                <Chip label={t('media.upload.completed', { count: completedCount })} color="success" size="small" />
              )}
              {errorCount > 0 && (
                <Chip label={t('media.upload.errors', { count: errorCount })} color="error" size="small" />
              )}
            </Box>
          </Box>
        )}

        {/* Список файлов */}
        {files.length > 0 && (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {files.map((fileUpload) => {
              const FileIcon = getFileIcon(fileUpload.file.type);

              return (
                <React.Fragment key={fileUpload.id}>
                  <ListItem>
                    <ListItemIcon>
                      <FileIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <TextField
                            size="small"
                            label={t('media.title')}
                            value={fileUpload.title}
                            onChange={(e) => handleUpdateFile(fileUpload.id, { title: e.target.value })}
                            disabled={fileUpload.status !== 'pending'}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label={t('media.description')}
                            value={fileUpload.description}
                            onChange={(e) => handleUpdateFile(fileUpload.id, { description: e.target.value })}
                            disabled={fileUpload.status !== 'pending'}
                            multiline
                            rows={2}
                            fullWidth
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {fileUpload.file.name} • {formatFileSize(fileUpload.file.size)} • {getFileTypeLabel(fileUpload.file.type)}
                          </Typography>

                          {fileUpload.status === 'uploading' && (
                            <Box sx={{ mt: 1 }}>
                              <LinearProgress variant="determinate" value={fileUpload.progress} />
                              <Typography variant="caption" color="text.secondary">
                                {Math.round(fileUpload.progress)}%
                              </Typography>
                            </Box>
                          )}

                          {fileUpload.status === 'error' && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {fileUpload.error}
                            </Alert>
                          )}

                          {fileUpload.status === 'completed' && (
                            <Alert severity="success" sx={{ mt: 1 }}>
                              {t('media.upload.success')}
                            </Alert>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {fileUpload.status === 'pending' && (
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFile(fileUpload.id)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                      {fileUpload.status === 'completed' && (
                        <CheckCircle color="success" />
                      )}
                      {fileUpload.status === 'error' && (
                        <Error color="error" />
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              );
            })}
          </List>
        )}

        {files.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
            {t('media.upload.noFiles')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleUploadAll}
          disabled={pendingCount === 0 || uploadingCount > 0}
          startIcon={<CloudUpload />}
        >
          {t('media.upload.uploadAll', { count: pendingCount })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog;
